import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { getClientsAnalytics, getClientsCities } from '../../../shared/api/clientsAnalytics.api'
import type {
  AnalyticsFilter,
  AnalyticsMode,
  ClientsAnalyticsItem,
  ClientsAnalyticsResponse,
} from '@crm/shared'
import type { DashboardPeriodKey } from './types'
import type { ClientsAnalyticsState } from './clientsAnalyticsState'

interface Props {
  periodKey: DashboardPeriodKey
  customFrom: string | null
  customTo: string | null
  state: ClientsAnalyticsState
}

// Палитра для секторов. Циклится при большом количестве категорий.
const SECTOR_PALETTE = [
  '#2563eb',
  '#16a34a',
  '#dc2626',
  '#7c3aed',
  '#ea580c',
  '#0891b2',
  '#db2777',
  '#ca8a04',
  '#0d9488',
  '#9333ea',
]

const OTHER_LABEL = 'Прочее'
const NO_CITY_LABEL = 'Без города'

const MODE_LABELS: Record<AnalyticsMode, { tab: string; centerLabel: string; noun: string }> = {
  matrix: { tab: 'Матрица (₽)', centerLabel: '₽ всего', noun: '₽' },
  category: { tab: 'Категория A/B/C', centerLabel: 'клиентов', noun: 'клиентов' },
  segment: { tab: 'Сегмент', centerLabel: 'клиентов', noun: 'клиентов' },
  city: { tab: 'Город', centerLabel: 'клиентов', noun: 'клиентов' },
}

const FILTER_LABEL: Record<string, string> = {
  category: 'Категория',
  segment: 'Сегмент',
  city: 'Город',
}

function formatValue(item: ClientsAnalyticsItem, unit: 'rub' | 'clients'): string {
  if (unit === 'rub') return `${item.value.toLocaleString('ru-RU')} ₽`
  return `${item.value} клиентов`
}

function buildQuery(
  periodKey: DashboardPeriodKey,
  customFrom: string | null,
  customTo: string | null,
): {
  period: 'day' | 'week' | 'month' | 'quarter' | 'year' | 'custom'
  from?: string
  to?: string
} | null {
  if (periodKey === 'custom') {
    if (!customFrom || !customTo) return null
    return { period: 'custom', from: customFrom, to: customTo }
  }
  return { period: periodKey }
}

export function ClientsDonutCard({ periodKey, customFrom, customTo, state }: Props) {
  const periodQuery = useMemo(
    () => buildQuery(periodKey, customFrom, customTo),
    [periodKey, customFrom, customTo],
  )

  const enabled = Boolean(periodQuery)
  const { data, isLoading } = useQuery({
    queryKey: ['clients-analytics', state.mode, state.filters, periodQuery],
    queryFn: () =>
      getClientsAnalytics({
        ...periodQuery!,
        mode: state.mode,
        managerIds: [],
        filters: state.filters,
      }),
    enabled,
  })

  if (!periodQuery) {
    return (
      <Card>
        <Empty>Аналитика доступна для периодов «Месяц», «Квартал», «Год» и «Период…».</Empty>
      </Card>
    )
  }

  return (
    <Card>
      <Header state={state} />
      <ActiveFilters state={state} />
      <QuickFilters state={state} />
      {isLoading || !data ? <Empty>Загрузка…</Empty> : <DonutAndLegend data={data} state={state} />}
    </Card>
  )
}

// ── Сабкомпоненты ────────────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-4 flex flex-col gap-3">
      {children}
    </div>
  )
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-gray-500 px-2 py-8 text-center">{children}</div>
}

function Header({ state }: { state: ClientsAnalyticsState }) {
  const modes: AnalyticsMode[] = ['matrix', 'category', 'segment', 'city']
  return (
    <div className="flex items-center justify-between flex-wrap gap-2">
      <div className="text-sm text-gray-500">Аналитика клиентской базы</div>
      <div className="inline-flex rounded-xl border border-gray-200 overflow-hidden">
        {modes.map((m) => (
          <button
            key={m}
            onClick={() => state.setMode(m)}
            className={`px-3 py-1.5 text-xs transition-colors ${
              state.mode === m
                ? 'bg-gray-900 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            {MODE_LABELS[m].tab}
          </button>
        ))}
      </div>
    </div>
  )
}

function ActiveFilters({ state }: { state: ClientsAnalyticsState }) {
  if (state.filters.length === 0) return null
  return (
    <div className="flex items-center gap-2 flex-wrap text-xs">
      <span className="text-gray-400">Активные фильтры:</span>
      {state.filters.map((f) => (
        <span
          key={f.mode}
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-brand-50 text-brand-700 rounded-md border border-brand-100"
        >
          {FILTER_LABEL[f.mode]}: {f.value}
          <button
            onClick={() => state.removeFilter(f.mode)}
            className="hover:bg-brand-100 rounded-sm px-0.5 leading-none"
            aria-label="Убрать фильтр"
          >
            ✕
          </button>
        </span>
      ))}
      <button
        onClick={state.clearFilters}
        className="text-gray-500 hover:text-gray-900 underline underline-offset-2"
      >
        Сбросить все
      </button>
    </div>
  )
}

// Кнопка-чип фильтра. on — активна.
function ChipButton({
  on,
  onClick,
  children,
}: {
  on: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={`px-2 py-0.5 rounded-md border transition-colors ${
        on
          ? 'bg-brand-50 border-brand-200 text-brand-700'
          : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  )
}

function QuickFilters({ state }: { state: ClientsAnalyticsState }) {
  const catActive = state.filters.find((f) => f.mode === 'category')
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-gray-400 w-20">Категория:</span>
        {['A', 'B', 'C'].map((v) => {
          const on = catActive?.value === v
          return (
            <ChipButton
              key={v}
              on={on}
              onClick={() => (on ? state.removeFilter('category') : state.addFilter('category', v))}
            >
              {v}
            </ChipButton>
          )
        })}
      </div>
      <CityFilterRow state={state} />
    </div>
  )
}

/**
 * Динамический ряд кнопок-городов. Города подтягиваются с бэка
 * (нормализованные), сортированы по числу клиентов. Города с < 2 клиентами
 * прячутся под кнопку «Прочее» (раскрывается списком). Плюс «Все города»
 * (снять фильтр) и «Без города».
 */
function CityFilterRow({ state }: { state: ClientsAnalyticsState }) {
  const [expanded, setExpanded] = useState(false)
  const { data } = useQuery({
    queryKey: ['clients-cities'],
    queryFn: getClientsCities,
  })
  const active = state.filters.find((f) => f.mode === 'city')
  const activeCity = active?.value ?? null

  const big = useMemo(() => (data?.cities ?? []).filter((c) => c.count >= 2), [data])
  const small = useMemo(() => (data?.cities ?? []).filter((c) => c.count < 2), [data])
  // Если выбран мелкий город (через «Прочее») — покажем его отдельной кнопкой.
  const activeIsSmall =
    activeCity != null && activeCity !== NO_CITY_LABEL && small.some((c) => c.city === activeCity)

  const toggleCity = (city: string) => {
    if (activeCity === city) state.removeFilter('city')
    else state.addFilter('city', city)
  }

  return (
    <div className="flex items-start gap-2 flex-wrap text-xs">
      <span className="text-gray-400 w-20 pt-0.5">Город:</span>
      <div className="flex items-center gap-1.5 flex-wrap flex-1">
        <ChipButton on={!activeCity} onClick={() => state.removeFilter('city')}>
          Все города
        </ChipButton>
        {big.map((c) => (
          <ChipButton key={c.city} on={activeCity === c.city} onClick={() => toggleCity(c.city)}>
            {c.city} <span className="text-gray-400">{c.count}</span>
          </ChipButton>
        ))}
        {activeIsSmall && (
          <ChipButton on onClick={() => toggleCity(activeCity!)}>
            {activeCity}
          </ChipButton>
        )}
        {data && data.noCity > 0 && (
          <ChipButton on={activeCity === NO_CITY_LABEL} onClick={() => toggleCity(NO_CITY_LABEL)}>
            Без города <span className="text-gray-400">{data.noCity}</span>
          </ChipButton>
        )}
        {small.length > 0 && (
          <ChipButton on={expanded} onClick={() => setExpanded((v) => !v)}>
            Прочее <span className="text-gray-400">{small.length}</span> {expanded ? '▴' : '▾'}
          </ChipButton>
        )}
        {expanded &&
          small.map((c) => (
            <ChipButton key={c.city} on={activeCity === c.city} onClick={() => toggleCity(c.city)}>
              {c.city}
            </ChipButton>
          ))}
      </div>
    </div>
  )
}

function DonutAndLegend({
  data,
  state,
}: {
  data: ClientsAnalyticsResponse
  state: ClientsAnalyticsState
}) {
  const navigate = useNavigate()
  if (data.items.length === 0) {
    return <Empty>Нет данных под выбранные фильтры. Попробуйте убрать один.</Empty>
  }

  const items = data.items.slice(0, 20)
  // Клик по сектору ведёт в /clients со всеми активными фильтрами + клик-ключ
  // (если режим = category/segment/city). matrix-mode сектора не кликабельны.
  // Сектор «Прочее» (свёрнутые мелкие города) тоже не кликабелен — точного
  // фильтра по нему в /clients нет.
  const sectorClickable = data.mode !== 'matrix'
  const isClickableKey = (key: string) => sectorClickable && key !== OTHER_LABEL
  const navigateToClients = (sectorKey: string | null) => {
    if (!sectorClickable) return
    if (sectorKey === OTHER_LABEL) return
    const params = new URLSearchParams()
    for (const f of state.filters) params.set(f.mode, f.value)
    if (sectorKey) params.set(data.mode, sectorKey)
    navigate(`/clients?${params.toString()}`)
  }
  // Drill-down остаётся на легенде (как в моке Сергея).
  const onLegendClick = (key: string) => {
    if (state.drilldownDisabled) return
    if (data.mode === 'matrix') return
    if (key === OTHER_LABEL) return
    state.addFilter(data.mode as 'category' | 'segment' | 'city', key)
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 items-center">
      <div className="relative h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={items}
              dataKey="value"
              nameKey="key"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={1}
              isAnimationActive={false}
            >
              {items.map((it, idx) => (
                <Cell
                  key={it.key}
                  fill={SECTOR_PALETTE[idx % SECTOR_PALETTE.length]!}
                  onClick={() => navigateToClients(it.key)}
                  cursor={isClickableKey(it.key) ? 'pointer' : 'default'}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(_v, _n, p) => {
                const it = p?.payload as ClientsAnalyticsItem | undefined
                if (!it) return ['', '']
                const hint = isClickableKey(it.key) ? ' · клик → к клиентам' : ''
                return [formatValue(it, data.unit), `${it.key} · ${it.percent.toFixed(1)}%${hint}`]
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div className="text-2xl font-bold text-gray-900">
            {data.unit === 'rub'
              ? `${(data.total / 1000).toFixed(data.total >= 1_000_000 ? 0 : 1)}К`
              : data.total}
          </div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mt-1">
            {MODE_LABELS[data.mode].centerLabel}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1.5 text-sm max-h-[280px] overflow-y-auto">
        {items.map((it, idx) => {
          const clickable =
            !state.drilldownDisabled && data.mode !== 'matrix' && it.key !== OTHER_LABEL
          return (
            <button
              key={it.key}
              onClick={() => onLegendClick(it.key)}
              disabled={!clickable}
              className={`flex items-center gap-2 px-2 py-1 rounded-md text-left ${
                clickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
              }`}
              title={clickable ? `Добавить фильтр ${data.mode}=${it.key}` : undefined}
            >
              <span
                className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                style={{ background: SECTOR_PALETTE[idx % SECTOR_PALETTE.length] }}
              />
              <span className="flex-1 truncate text-gray-700">{it.key}</span>
              <span className="text-xs text-gray-500 tabular-nums">
                {formatValue(it, data.unit)}
              </span>
              <span className="text-xs text-gray-400 tabular-nums w-12 text-right">
                {it.percent.toFixed(1)}%
              </span>
            </button>
          )
        })}
        {sectorClickable && (
          <div className="text-xs text-gray-400 italic mt-2 px-2">
            Клик по сектору → открыть список клиентов с этими фильтрами.
            {!state.drilldownDisabled && ' Клик по легенде → добавить фильтр в диаграмму.'}
          </div>
        )}
      </div>
    </div>
  )
}

// Использование AnalyticsFilter в типе пропсов вне функции — чтобы tsc не ругался.
export type { AnalyticsFilter }
